import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";
import { MeSubscription } from "@cohesive/api-client";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Valid subscription statuses that allow access to the app.
 * - active: User has an active paid subscription
 * - trialing: User is on a free trial (for grandfathered users with trial)
 * - past_due: User has a past due subscription but still has access
 */
const VALID_SUBSCRIPTION_STATUSES = ["active", "trialing", "past_due"];

/**
 * Routes that are exempt from the subscription check.
 * Users can access these routes even without a subscription.
 */
const EXEMPT_ROUTE_PREFIXES = [
  ROUTES.ONBOARDING.ROOT, // All onboarding routes
  ROUTES.BILLING.ROOT, // All billing routes (checkout, plans, etc.)
  ROUTES.INVITE,
  ROUTES.AUTH.LOGIN, // Auth routes
  ROUTES.AUTH.LOGOUT,
  ROUTES.AUTH.ERROR,
  ROUTES.AUTH.REDIRECT,
  ROUTES.PAGES.MAINTENANCE,
];

/** Checkout/confirm routes where invite-provisioned trials enter card details. */
const TRIAL_CARD_GATE_ALLOWED_ROUTE_PREFIXES = [
  ROUTES.BILLING.CHECKOUT,
  ROUTES.BILLING.CONFIRM_SETUP,
  ROUTES.ONBOARDING.CHECKOUT,
  ROUTES.ONBOARDING.CONFIRM_SETUP,
];

function isTrialCardGateAllowedRoute(pathname: string): boolean {
  return TRIAL_CARD_GATE_ALLOWED_ROUTE_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );
}

function requiresTrialPaymentMethod(
  subscription: MeSubscription | undefined,
): boolean {
  return (
    subscription?.status === "trialing" &&
    subscription.trial_payment_method_required &&
    !subscription.default_payment_method_id
  );
}

/**
 * Hook that checks if the user has a valid subscription and redirects
 * to the plan selection page if they don't.
 *
 * This is used to enforce the paywall for new users who have created
 * an organization but haven't selected a plan yet.
 *
 * Grandfathered users (existing trial users) are not affected because
 * they already have a subscription with status "trialing".
 *
 * Uses the /me endpoint (via useUserInfo) which always returns subscription
 * info if available, avoiding 404 errors for new organizations.
 */
export function useRequireSubscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: userInfo, isLoading } = useUserInfo();

  // Get subscription from userInfo (from /me endpoint)
  const subscription = userInfo?.subscription;

  useEffect(() => {
    // Don't check while loading
    if (isLoading) return;

    // Invite-provisioned trials can require a card before app access. This gate
    // runs on billing routes too (they are exempt from the checks below), so
    // /billing/plans cannot bypass checkout. Checkout/confirm routes stay
    // reachable so users can add a card.
    if (
      subscription &&
      requiresTrialPaymentMethod(subscription) &&
      !isTrialCardGateAllowedRoute(location.pathname)
    ) {
      navigate(
        route(
          ROUTES.BILLING.CHECKOUT,
          {},
          {
            plan: subscription.plan_name,
            billing_cycle: subscription.billing_interval,
            user_count: String(subscription.seat_count),
            keep_trial: "1",
          },
        ),
        { replace: true },
      );
      return;
    }

    // Check if current path is exempt from subscription check
    const isExemptRoute = EXEMPT_ROUTE_PREFIXES.some((prefix) =>
      location.pathname.startsWith(prefix),
    );

    if (isExemptRoute) return;

    // No subscription at all - redirect to plan selection
    if (!subscription) {
      navigate(ROUTES.ONBOARDING.SELECT_PLAN, { replace: true });
      return;
    }

    // Check subscription status
    const status = subscription.status;
    const hasValidSubscription = VALID_SUBSCRIPTION_STATUSES.includes(
      status || "",
    );

    if (!hasValidSubscription) {
      // Subscription exists but is in an invalid state (incomplete, canceled, etc.)
      navigate(ROUTES.ONBOARDING.SELECT_PLAN, { replace: true });
      return;
    }
  }, [subscription, isLoading, location.pathname, navigate]);

  return {
    isLoading,
    hasValidSubscription: subscription
      ? VALID_SUBSCRIPTION_STATUSES.includes(subscription.status || "")
      : false,
  };
}
